import React from 'react';
import { Paperclip, Upload, FileText, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { MAX_COMPROBANTES, MAX_FILE_MB, isPdfUrl, formatCurrency } from './utils';

/**
 * Diálogo minimalista para EDITAR un pago existente.
 * (El diálogo `PagoDialog` es el flujo NUEVO por cliente; este es solo edición.)
 */
export const PagoEditDialog = ({
  open, onOpenChange,
  formPago, setFormPago,
  facturaAsociada,
  comprobantes,
  onSelectComprobantes,
  onRemoveComprobante,
  uploadingComprobante,
  saving, onSave,
}) => {
  const total = Number(facturaAsociada?.total_monto || 0);
  const saldo = Number(facturaAsociada?.saldo || 0);
  const abonado = Math.max(0, total - saldo);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="modal-editar-pago">
        <DialogHeader>
          <DialogTitle>Editar Pago</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {facturaAsociada && (
            <div className="rounded-lg border bg-muted/40 p-3">
              <div className="text-xs text-muted-foreground">Factura</div>
              <div className="font-semibold">{facturaAsociada.numero_factura} · {facturaAsociada.cliente_nombre}</div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center text-xs">
                <div>
                  <div className="text-muted-foreground">Total</div>
                  <div className="font-semibold">{formatCurrency(total)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Abonado</div>
                  <div className="font-semibold text-emerald-700">{formatCurrency(abonado)}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Saldo</div>
                  <div className="font-semibold text-destructive">{formatCurrency(saldo)}</div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={formPago.fecha}
                onChange={(e) => setFormPago({ ...formPago, fecha: e.target.value })}
                className="h-10 mt-1.5"
              />
            </div>
            <div>
              <Label>Monto ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formPago.monto}
                onChange={(e) => setFormPago({ ...formPago, monto: e.target.value })}
                className="h-10 mt-1.5 tabular-nums"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Método de Pago</Label>
              <Select
                value={formPago.metodo_pago}
                onValueChange={(v) => setFormPago({ ...formPago, metodo_pago: v })}
              >
                <SelectTrigger className="h-10 mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referencia</Label>
              <Input
                placeholder="Ej: #TRX-982"
                value={formPago.referencia}
                onChange={(e) => setFormPago({ ...formPago, referencia: e.target.value })}
                className="h-10 mt-1.5"
              />
            </div>
          </div>

          <div>
            <Label>Observaciones</Label>
            <Input
              placeholder="Notas adicionales"
              value={formPago.observaciones}
              onChange={(e) => setFormPago({ ...formPago, observaciones: e.target.value })}
              className="h-10 mt-1.5"
            />
          </div>

          {/* Comprobantes */}
          {onSelectComprobantes && (
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Comprobantes ({comprobantes.length}/{MAX_COMPROBANTES})
                </Label>
                <span className="text-[10px] text-muted-foreground">
                  JPG, PNG, PDF · máx. {MAX_FILE_MB} MB c/u
                </span>
              </div>
              {comprobantes.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-2">
                  {comprobantes.map((c, idx) => {
                    const isPdf = (c.content_type || '').includes('pdf') || isPdfUrl(c.url);
                    return (
                      <div key={c.url || idx} className="relative border rounded-md overflow-hidden bg-muted/40">
                        {isPdf ? (
                          <div className="h-20 flex flex-col items-center justify-center text-muted-foreground">
                            <FileText className="w-6 h-6" />
                            <span className="text-[10px] mt-1 px-1 truncate w-full text-center">{c.filename || 'PDF'}</span>
                          </div>
                        ) : (
                          <a href={c.url} target="_blank" rel="noopener noreferrer">
                            <img src={c.url} alt={c.filename || `Comp ${idx + 1}`} className="h-20 w-full object-cover" loading="lazy" />
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => onRemoveComprobante(idx)}
                          disabled={uploadingComprobante || saving}
                          className="absolute top-1 right-1 bg-black/60 hover:bg-destructive text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {comprobantes.length < MAX_COMPROBANTES && (
                <label
                  className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border border-dashed cursor-pointer text-xs transition-colors ${
                    uploadingComprobante ? 'opacity-50 pointer-events-none' : 'hover:bg-accent'
                  }`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingComprobante ? 'Subiendo...' : 'Adjuntar comprobante'}
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf"
                    multiple
                    className="hidden"
                    onChange={onSelectComprobantes}
                    disabled={uploadingComprobante}
                  />
                </label>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving || uploadingComprobante}>
            Cancelar
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || uploadingComprobante || !(parseFloat(formPago.monto) > 0)}
          >
            {saving ? 'Actualizando…' : 'Actualizar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PagoEditDialog;
